import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, useMap, GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Skeleton from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';

// Fix for default marker icons in Leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Component to set the initial view and map bounds
const SetMapBounds = () => {
  const map = useMap();
  
  useEffect(() => {
    // Set view to center of Punjab
    map.fitBounds([
      [32.5007, 74.5237], // North-West
      [29.3822, 71.5249]  // South-East
    ]);
  }, [map]);
  
  return null;
};

// Component to render district labels
const DistrictLabels = ({ districts }) => {
  const map = useMap();

  useEffect(() => {
    if (!districts) return;

    // Create a layer group for labels
    const labelLayer = L.layerGroup().addTo(map);

    districts.features.forEach(feature => {
      if (feature.properties?.district_n) {
        // Calculate centroid for the district
        const coordinates = feature.geometry.coordinates;
        let centerLat = 0, centerLng = 0, pointCount = 0;

        // Handle both Polygon and MultiPolygon
        const polygons = feature.geometry.type === 'Polygon' ? [coordinates] : coordinates;
        
        polygons.forEach(polygon => {
          polygon[0].forEach(coord => {
            centerLng += coord[0];
            centerLat += coord[1];
            pointCount++;
          });
        });

        if (pointCount > 0) {
          centerLat /= pointCount;
          centerLng /= pointCount;

          // Create the label
          const label = L.marker([centerLat, centerLng], {
            icon: L.divIcon({
              className: 'district-label',
              html: `<div style="
                      display: inline-block;
                      background: rgba(255, 255, 255, 0);
                      padding: 2px 8px;
                      border-radius: 4px;
                      font-weight: bold;
                      font-size: 14px;
                      color: #5e0404be;
                      text-shadow: 1px 1px 1px white;
                      white-space: nowrap;
                    ">
                      ${feature.properties.district_n}
                    </div>`,
              iconSize: [0, 0],
              iconAnchor: [0, 0]
            }),
            zIndexOffset: -1000
          });

          label.addTo(labelLayer);
        }
      }
    });

    return () => {
      map.removeLayer(labelLayer);
    };
  }, [map, districts]);

  return null;
};

// Component to render volunteer count markers
const VolunteerMarkers = ({ volunteerData }) => {
  const map = useMap();

  useEffect(() => {
    if (!volunteerData) return;

    const markerLayer = L.layerGroup().addTo(map);

    // Iterate through all volunteer data points (up to ~40 points)
    volunteerData.forEach(({ district, count, lat, lon }) => {
      // Validate lat and lon to ensure they are numbers and within reasonable bounds
      if (typeof lat === 'number' && typeof lon === 'number' && 
          lat >= -90 && lat <= 90 && lon >= -180 && lon <= 180) {
        const marker = L.marker([lat, lon], {
          icon: L.divIcon({
            className: 'volunteer-marker',
            html: `<div style="
                    background: #ff4444;
                    color: white;
                    padding: 4px 8px;
                    border-radius: 12px;
                    font-weight: bold;
                    font-size: 12px;
                    text-align: center;
                    box-shadow: 0 2px 4px rgba(0,0,0,0.3);
                    min-width: 30px;
                  ">
                    ${count}
                  </div>`,
            iconSize: [40, 20],
            iconAnchor: [20, 10]
          })
        });

        marker.bindPopup(`<b>${district}</b><br>Volunteers: ${count}`);
        marker.addTo(markerLayer);
      } else {
        console.warn(`Invalid coordinates for ${district}: lat=${lat}, lon=${lon}`);
      }
    });

    return () => {
      map.removeLayer(markerLayer);
    };
  }, [map, volunteerData]);

  return null;
};

const MapComponentVolunteer = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [districts, setDistricts] = useState(null);
  const [volunteerData, setVolunteerData] = useState(null);
  const API_BASE_URL = process.env.REACT_APP_BE_API_URL;

  // Load districts and volunteer data
  useEffect(() => {
    const loadDistricts = async () => {
      try {
        const response = await fetch(`${process.env.PUBLIC_URL}/assets/districts_punjab.geojson`);
        const data = await response.json();
        setDistricts(data);
      } catch (err) {
        console.error('Error loading GeoJSON:', err);
        setError(err.message);
      }
    };
    
    const loadVolunteerData = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/backend/volunteers/get-district-wise-count`);
        const result = await response.json();
        if (result.status) {
          // Ensure all ~40 points are loaded
          if (Array.isArray(result.data)) {
            setVolunteerData(result.data);
          } else {
            throw new Error('Invalid volunteer data format');
          }
        } else {
          throw new Error('API returned unsuccessful status');
        }
      } catch (err) {
        console.error('Error loading volunteer data:', err);
        setError(err.message);
      }
    };

    Promise.all([loadDistricts(), loadVolunteerData()]).finally(() => setLoading(false));
  }, [API_BASE_URL]);

  return (
    <div className="col-xxl-12">
      <div className="card h-100">
        <div className="card-body">
          <h6 className="fw-bold mb-0">Green Volunteers-District Map</h6>
          
          <div className="mt-2" style={{ height: '600px', borderRadius: '8px', overflow: 'hidden', position: 'relative' }}>
            {loading ? (
              <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 2 }}>
                <Skeleton height="100%" />
              </div>
            ) : error ? (
              <div className="alert alert-danger">{error}</div>
            ) : (
              <MapContainer 
                style={{ height: '100%', width: '100%' }} 
                scrollWheelZoom={true}
                preferCanvas={true}
              >
                <SetMapBounds />
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  attribution='Tiles © Esri'
                />
                <TileLayer
                  url="https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}"
                  attribution='Tiles © Esri'
                />
                
                {districts && (
                  <>
                    <GeoJSON
                      data={districts}
                      style={() => ({
                        color: '#3388ff',
                        weight: 2,
                        opacity: 1,
                        fillOpacity: 0.1
                      })}
                    />
                    <DistrictLabels districts={districts} />
                  </>
                )}
                {volunteerData && <VolunteerMarkers volunteerData={volunteerData} />}
              </MapContainer>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MapComponentVolunteer;